import { getCookie } from 'cookies-next'
import type { PostHog } from 'posthog-js'
import { DUPE_EXPOSURE_FLAGS_COOKIE_KEY } from '~/src/lib/constants'
import {
  isInterstitialPathname,
  makeFeatureFlagCalledFilter,
  parseExposureFlagsCookie,
} from '~/src/lib/posthogBrowserConfig'

/** The posthog-js `before_send` hook built from the exposure-flags cookie. */
export type ExposureFilter = ReturnType<typeof makeFeatureFlagCalledFilter>

/**
 * Mutable holder for the active exposure filter. posthog-js captures
 * `before_send` once at init, so callers indirect through a ref and refresh it
 * on route changes to pick up newly-launched experiments without a reload.
 * Structurally compatible with `React.MutableRefObject`.
 */
export interface ExposureFilterRef {
  current: ExposureFilter | null
}

/**
 * Rebuild the `before_send` exposure filter from the current
 * `__dupe_exposure_flags` cookie. Called at init and on each route change so a
 * long-lived tab adopts a newly-launched experiment's allowlist without a full
 * reload. A missing/unknown cookie yields a keep_all filter (safe — never drops
 * exposures).
 */
export function buildExposureFilter(): ExposureFilter {
  return makeFeatureFlagCalledFilter(
    parseExposureFlagsCookie(
      getCookie(DUPE_EXPOSURE_FLAGS_COOKIE_KEY) as string | undefined
    )
  )
}

export interface PageviewIdentity {
  tempUserId?: string
  authUserId?: string
}

export interface PageviewProperties {
  __dupe_temp_user_id?: string
  __dupe_auth_user_id?: string
  _ga?: string
  _gcl_aw?: string
}

/**
 * Property bag attached to every client-emitted `$pageview`. User ids come
 * from the caller (auth state); the `_ga` / `_gcl_aw` ad-attribution cookies
 * are read at call time. Falsy values are omitted.
 */
export function buildPageviewProperties({
  tempUserId,
  authUserId,
}: PageviewIdentity): PageviewProperties {
  const _ga = getCookie('_ga') as string | undefined
  const _gcl_aw = getCookie('_gcl_aw') as string | undefined

  return {
    ...(tempUserId ? { __dupe_temp_user_id: tempUserId } : {}),
    ...(authUserId ? { __dupe_auth_user_id: authUserId } : {}),
    ...(_ga ? { _ga } : {}),
    ...(_gcl_aw ? { _gcl_aw } : {}),
  }
}

export interface RouteChangeSideEffectsDeps {
  /** The posthog-js singleton (or a stub in tests). */
  posthog: Pick<PostHog, 'capture'>
  /** Holder for the active `before_send` exposure filter (refreshed here). */
  exposureFilterRef: ExposureFilterRef
  /** `$pageview` property bag (see {@link buildPageviewProperties}). */
  properties: PageviewProperties
  /** Podscribe device id — auth uid when present, else the temp user id. */
  deviceId?: string
}

/**
 * Client-side soft-navigation side effects shared by both routers (pages
 * `routeChangeComplete` and the App Router pathname-change tracker): refresh
 * the exposure-filter allowlist, emit the PostHog `$pageview`, and ping the
 * third-party pixels when their scripts are present on `window`. Initial-load
 * pageviews are NOT handled here — posthog.init's default `capture_pageview`
 * and each pixel's inline bootstrap snippet cover those.
 */
export function trackRouteChangeComplete({
  posthog,
  exposureFilterRef,
  properties,
  deviceId,
}: RouteChangeSideEffectsDeps): void {
  // Refresh the exposure allowlist from the (middleware-updated) cookie so a
  // long-lived session adopts newly-launched experiments without a reload.
  exposureFilterRef.current = buildExposureFilter()

  posthog.capture('$pageview', properties)

  // A soft navigation onto `/redirect` must not hand its replayable address to
  // the pixels either — see `thirdPartyTagsAllowed` in AppProviders.
  if (isInterstitialPathname(window.location.pathname)) return

  // Facebook Pixel - track page view
  // https://developers.facebook.com/docs/pages/official-events/pixel-implementation-guide/
  if (window?.fbq) {
    window.fbq('track', 'PageView')
  }

  // Podscribe - track page view
  if (window?.podscribe) {
    window?.podscribe('view', { device_id: deviceId })
  }
}
